import { StateCreator } from 'zustand';

import { TypesOperation, TypesOperationsSliceProps } from './types-operations.types.ts';

export const createTypesOperationsSlice: StateCreator<TypesOperationsSliceProps> = (set, get) => ({
  addOperation: operation => {
    const newOperation: TypesOperation = {
      ...operation,
      id: `${operation.type}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      targetId: operation.type.includes('edit')
        ? operation.data.pathResName
        : operation.data.categoryPath,
      timestamp: Date.now(),
    };

    set(state => {
      const existingOperationIndex = state.pendingOperations.findIndex(
        op => op.targetId === newOperation.targetId && op.type === newOperation.type,
      );

      if (existingOperationIndex === -1) {
        return { pendingOperations: [...state.pendingOperations, newOperation] };
      } else {
        const updatedOperations = [...state.pendingOperations];
        updatedOperations[existingOperationIndex] = newOperation;
        return { pendingOperations: updatedOperations };
      }
    });
  },
  addTemporarySubtype: operation => {
    const newTemporarySubtype: TypesOperation = {
      ...operation,
      id: `temporary_subtype_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      targetId: operation.data.categoryPath,
      timestamp: Date.now(),
    };

    set(state => ({
      temporarySubtypes: [...state.temporarySubtypes, newTemporarySubtype],
    }));
  },
  addTemporaryType: operation => {
    const newTemporaryType: TypesOperation = {
      ...operation,
      id: `temporary_type_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      targetId: operation.data.categoryPath,
      timestamp: Date.now(),
    };

    set(state => ({
      temporaryTypes: [...state.temporaryTypes, newTemporaryType],
    }));
  },

  clearOperations: () => {
    set({ pendingOperations: [] });
  },

  clearTemporarySubtypes: () => {
    set({ temporarySubtypes: [] });
  },

  clearTemporaryTypes: () => {
    set({ temporaryTypes: [] });
  },

  getTemporarySubtypesForCategory: (categoryPath: string) => {
    const state = get();
    return state.temporarySubtypes.filter(op => op.data.parentCategoryPath === categoryPath);
  },

  getTemporaryTypes: () => {
    const state = get();
    return state.temporaryTypes;
  },

  getUniqueOperationsCount: () => {
    const state = get();
    const uniqueTargets = new Set();

    state.pendingOperations.forEach(op => {
      if (op.targetId) {
        uniqueTargets.add(`${op.type}_${op.targetId}`);
      }
    });

    return uniqueTargets.size;
  },

  hasPendingChanges: (targetId: string, type: string) => {
    const state = get();
    return state.pendingOperations.some(op => op.targetId === targetId && op.type === type);
  },

  pendingOperations: [],

  removeOperation: id => {
    set(state => ({
      pendingOperations: state.pendingOperations.filter(op => op.id !== id),
    }));
  },

  removeTemporarySubtype: id => {
    set(state => ({
      temporarySubtypes: state.temporarySubtypes.filter(op => op.id !== id),
    }));
  },

  removeTemporaryType: id => {
    set(state => ({
      temporaryTypes: state.temporaryTypes.filter(op => op.id !== id),
    }));
  },

  temporarySubtypes: [],

  temporaryTypes: [],

  updateOperation: (id, updates) => {
    set(state => ({
      pendingOperations: state.pendingOperations.map(op =>
        op.id === id ? { ...op, ...updates } : op,
      ),
    }));
  },
});
